import { isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { type ExpressionType, type Filter, useFiltersStore } from '../../../../../../../../stores/filters';
import {
  formatLookerExpression,
  normalizeInput,
  parseLookerExpression,
} from '../../../../../../../HCPSiteWorkspaceView/helpers/utils';

interface UsePageURLFilterOptions {
  filterName: string;
  onChange: (filter: Filter) => void;
  isHCPSite?: boolean;
}

export const usePageURLFilter = ({ filterName, onChange, isHCPSite = false }: UsePageURLFilterOptions) => {
  const initialState = useMemo(() => {
    const state = useFiltersStore.getState();
    const filterState = state.filters[filterName];
    const draftState = state.draftFilters[filterName];

    const isDirty = draftState !== undefined && !isEqual(draftState, filterState);
    const filterValue = isDirty ? draftState : filterState;

    const rawExpression = filterValue?.expression ?? '';
    const filterExpression = isHCPSite ? parseLookerExpression(rawExpression) : rawExpression;
    const filterEnabled = !!filterValue?.enabled && (isHCPSite || !!rawExpression);
    const storeExpressionType = (filterValue?.expressionType === 'block' ? 'block' : 'target') as ExpressionType;

    // Convert comma-separated values to newline-separated for line-by-line rendering
    const formattedExpression = filterExpression ? filterExpression.replace(/,/g, '\n') : '';

    return {
      inputValue: formattedExpression,
      isEnabled: filterEnabled,
      expressionType: storeExpressionType,
      isEditMode: isDirty || !rawExpression,
    };
  }, [filterName, isHCPSite]);

  const [inputValue, setInputValue] = useState<string>(initialState.inputValue);
  const [isPageURLControlEnabled, setIsPageURLControlEnabled] = useState<boolean>(initialState.isEnabled);
  const [expressionType, setExpressionType] = useState<ExpressionType>(initialState.expressionType);
  const [isEditMode, setIsEditMode] = useState<boolean>(initialState.isEditMode);

  const getExpression = useCallback(
    (value: string, expType: ExpressionType): string => {
      if (isHCPSite) return formatLookerExpression(value, expType);
      return normalizeInput(value);
    },
    [isHCPSite],
  );

  const updateStore = useCallback(
    (filter: Filter) => {
      if (!isHCPSite) {
        onChange(filter);
      } else {
        useFiltersStore.getState().setDraftFilter(filter);
      }
    },
    [isHCPSite, onChange],
  );

  const handleInputChange = (value: string | number) => {
    const valString = value.toString();
    setInputValue(valString);

    if (!isPageURLControlEnabled) return;

    const filter: Filter = {
      filterName,
      expression: getExpression(valString, expressionType),
      enabled: true,
      expressionType,
    };

    updateStore(filter);
  };

  const handleApply = () => {
    if (!isPageURLControlEnabled) return;

    const filter: Filter = {
      filterName,
      expression: getExpression(inputValue, expressionType),
      enabled: true,
      expressionType,
    };

    onChange(filter);
    setIsEditMode(false);

    if (isHCPSite) {
      useFiltersStore.getState().clearDraftFilter(filterName);
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setIsPageURLControlEnabled(checked);

    const filter: Filter = {
      filterName,
      expression: checked ? getExpression(inputValue.toString(), expressionType) : '',
      enabled: checked,
      expressionType,
    };

    updateStore(filter);

    if (checked) {
      setIsEditMode(true);
    } else {
      setInputValue('');
    }
  };

  const handleRadioChange = (value: ExpressionType) => {
    setExpressionType(value);

    const filter: Filter = {
      filterName,
      expression: getExpression(inputValue, value),
      enabled: true,
      expressionType: value,
    };

    updateStore(filter);
  };

  const appliedItems = inputValue
    ? inputValue
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

  const handleEdit = useCallback(() => setIsEditMode(true), []);

  return {
    inputValue,
    isPageURLControlEnabled,
    expressionType,
    isEditMode,
    appliedItems,
    handleInputChange,
    handleCheckboxChange,
    handleRadioChange,
    handleApply,
    handleEdit,
  };
};
